import React, { useMemo, useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import { ChevronsUpDown, Image as ImageIcon, Loader2 } from 'lucide-react'
import { supabase } from '@/lib/supabase'
import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import { Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList } from '@/components/ui/command'
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover'

interface GallerySelectProps {
  value?: number
  onChange: (value: number | null) => void
  disabled?: boolean
  placeholder?: string
}

interface GallerySummary {
  id: number
  title: string
  franchise_region: string | null
  job_number: string | null
  review_status?: string | null
  cover_image?: string | null
}

async function fetchGalleries(search: string): Promise<GallerySummary[]> {
  let query = supabase
    .from('galleries')
    .select('id, title, franchise_region, job_number, review_status, cover_image')
    .order('created_at', { ascending: false })
    .limit(50)

  if (search) {
    query = query.or(`title.ilike.%${search}%,franchise_region.ilike.%${search}%`)
  }

  const { data, error } = await query
  if (error) throw error
  return data || []
}

async function fetchGalleryById(id?: number | null): Promise<GallerySummary | null> {
  if (!id) return null

  const { data, error } = await supabase
    .from('galleries')
    .select('id, title, franchise_region, job_number, review_status, cover_image')
    .eq('id', id)
    .single()

  if (error) throw error
  return data
}

export default function GallerySelect({ value, onChange, disabled = false, placeholder = 'Search galleries' }: GallerySelectProps) {
  const [open, setOpen] = useState(false)
  const [searchTerm, setSearchTerm] = useState('')

  const { data: galleries, isLoading } = useQuery({
    queryKey: ['galleries', searchTerm],
    queryFn: () => fetchGalleries(searchTerm),
    enabled: open && !disabled
  })

  const { data: selectedGallery, isLoading: isSelectedLoading } = useQuery({
    queryKey: ['gallery', value],
    queryFn: () => fetchGalleryById(value),
    enabled: !!value
  })

  const buttonLabel = useMemo(() => {
    if (isSelectedLoading) return 'Loading gallery...'
    if (selectedGallery) return `${selectedGallery.title} (#${selectedGallery.id})`
    return 'Select a gallery'
  }, [selectedGallery, isSelectedLoading])

  if (disabled) {
    return (
      <div className="rounded-md border bg-muted/40 px-3 py-2 text-sm text-muted-foreground">
        {buttonLabel}
      </div>
    )
  }

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          type="button"
          variant="outline"
          role="combobox"
          aria-expanded={open}
          className="w-full justify-between"
        >
          <span className="truncate text-left">{buttonLabel}</span>
          <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-[420px] p-0" align="start">
        <Command>
          <CommandInput
            placeholder={placeholder}
            value={searchTerm}
            onValueChange={setSearchTerm}
          />
          <CommandList>
            {isLoading ? (
              <div className="flex items-center justify-center gap-2 py-6 text-sm text-muted-foreground">
                <Loader2 className="h-4 w-4 animate-spin" />
                Loading galleries...
              </div>
            ) : (
              <>
                <CommandEmpty>No galleries found.</CommandEmpty>
                <CommandGroup>
                  {(galleries || []).map((gallery) => (
                    <CommandItem
                      key={gallery.id}
                      value={gallery.title}
                      onSelect={() => {
                        onChange(gallery.id)
                        setOpen(false)
                      }}
                      className="flex items-center gap-3"
                    >
                      {gallery.cover_image ? (
                        <img
                          src={gallery.cover_image}
                          alt={gallery.title}
                          className="h-10 w-10 rounded object-cover"
                        />
                      ) : (
                        <div className="flex h-10 w-10 items-center justify-center rounded bg-muted">
                          <ImageIcon className="h-5 w-5 text-muted-foreground" />
                        </div>
                      )}
                      <div className="flex-1">
                        <p className="text-sm font-medium leading-tight">{gallery.title}</p>
                        <p className="text-xs text-muted-foreground">
                          {gallery.franchise_region?.replace('_', ' ') || 'Region TBD'}
                          {gallery.job_number && ` • Job #${gallery.job_number}`}
                        </p>
                      </div>
                      {gallery.review_status && (
                        <Badge variant={gallery.review_status === 'approved' ? 'default' : 'secondary'}>
                          {gallery.review_status}
                        </Badge>
                      )}
                    </CommandItem>
                  ))}
                </CommandGroup>
              </>
            )}
          </CommandList>
        </Command>
        {value && (
          <button
            type="button"
            className="w-full border-t px-3 py-2 text-left text-xs text-muted-foreground hover:bg-muted/50"
            onClick={() => {
              onChange(null)
              setOpen(false)
            }}
          >
            Clear selection
          </button>
        )}
      </PopoverContent>
    </Popover>
  )
}
